import type { AdvisoryAuditFinding, AdvisoryAuditState } from './advisoryAudit';
import type { AdvisoryQuoteReference } from './advisoryQuoteAnchors';

export type AdvisoryAnswerQuoteReferences = Readonly<
  Record<string, readonly AdvisoryQuoteReference[]>
>;

/** A chapter's color describes its own failures, not a dependency's findings. */
export function chapterNeedsAuditAttention(
  audit: AdvisoryAuditState | undefined,
  answerKey: string,
  references: readonly AdvisoryQuoteReference[]
): boolean {
  return (
    references.some(reference => reference.quote.status === 'absent') ||
    !!(
      audit &&
      'report' in audit &&
      audit.report.findings.some(
        finding => finding.answerKey === answerKey && finding.severity !== 'notice'
      )
    )
  );
}

/**
 * Findings currently identify an answer, not a source. Join quote checks only
 * when that answer has exactly one candidate and one corresponding finding.
 * Never infer source identity from a document title or the finding's prose.
 * S labels are assigned report-wide to a passage + quote + check in the Brief.
 */
export function groupAdvisoryAuditEvidence({
  findings,
  allFindings = findings,
  references,
  referencesByAnswer = {},
  answerKey,
}: {
  findings: readonly AdvisoryAuditFinding[];
  allFindings?: readonly AdvisoryAuditFinding[];
  references: readonly AdvisoryQuoteReference[];
  referencesByAnswer?: AdvisoryAnswerQuoteReferences;
  answerKey?: string;
}) {
  const groups = references.map(reference => ({
    reference,
    findings: [] as AdvisoryAuditFinding[],
  }));
  const answerFindings: AdvisoryAuditFinding[] = [];
  for (const finding of findings) {
    const status =
      finding.kind === 'absent_anchor'
        ? 'absent'
        : finding.kind === 'unchecked_anchor'
        ? 'unchecked'
        : null;
    const candidates = new Set(
      (referencesByAnswer[finding.answerKey] ?? [])
        .filter(reference => reference.quote.status === status)
        .map(reference => reference.label)
    );
    const correspondingFindings = allFindings.filter(
      other => other.answerKey === finding.answerKey && other.kind === finding.kind
    );
    const group =
      status && candidates.size === 1 && correspondingFindings.length === 1
        ? groups.find(item => candidates.has(item.reference.label))
        : undefined;
    if (group) group.findings.push(finding);
    else answerFindings.push(finding);
  }

  const scopedLabels = answerKey
    ? new Set((referencesByAnswer[answerKey] ?? []).map(reference => reference.label))
    : null;
  const priority = (group: (typeof groups)[number]) =>
    group.reference.quote.status === 'absent' ||
    group.findings.some(finding => finding.severity !== 'notice')
      ? 0
      : group.reference.quote.status !== 'verified' || group.findings.length
      ? 1
      : 2;
  return {
    sources: groups
      .filter(
        group => !scopedLabels || scopedLabels.has(group.reference.label) || group.findings.length
      )
      .sort((a, b) => priority(a) - priority(b)),
    answerFindings,
  };
}
